import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import NumberFormat from 'react-number-format';
import {
  Card,
  Checkbox,
  Dialog,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  Typography
} from '@material-ui/core';
import { states } from 'helpers/usStates';
import TermsAndConditions from 'components/StoreAddons/TermsAndConditions';
import { calculateMonthlySubscriptionByPeriod } from 'helpers/priceCalculations';


const StepTree = ({
  values,
  handleChange
}) => {
  const [openTerms, toggleOpenTerms] = useState(false);
  const {
    userProfile: {onboarding, marketing}
  } = useSelector((state) => state.subscription);
  const {plan} = marketing;
  const storeState = states.find((state) => state.value == values.storeInformation.stateId)?.shortName;
  const billingState = states.find((state) => state.value == values.billingInformation.state)?.shortName;

  const monthlySubscriptionPrice = calculateMonthlySubscriptionByPeriod({
    baseMonthlyPrice: onboarding.totalMonthlyPrice,
    addonsMonthlyPrice: marketing.totalMonthlyPrice, plan
  });

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Typography variant="h3" component="h3" className="h3 mb-5">
            You&apos;re almost there!
          </Typography>
          <Typography variant="h4" component="h4" className="h4">
            Review and purchase
          </Typography>
          <Typography variant="body1">
            {`This step allows you to review the form to confirm
            the form is populated completely and accurately,
              prior to certification and submission.`}
          </Typography>
        </Grid>
        <Grid item xs={12} md={9}>
          <Card elevation={25} className="p-5 mb-5">
            <Grid container spacing={3} className="p-4">
              <Grid item xs={12} md={6} className="flex-column">
                <Typography variant="h6" className="mb-4">
                  Store Information
                </Typography>
                <div className="pb-1">
                  {values.storeInformation.storeName}
                </div>
                {values.storeInformation.website &&
                  <div className="pb-1">
                    {values.storeInformation.website}
                  </div>
                }
                <div className="pb-1">
                  {values.storeInformation.streetAddress1}
                </div>
                {values.storeInformation.streetAddress2 &&
                  <div className="pb-1">
                    {values.storeInformation.streetAddress2}
                  </div>
                }
                <div className="pb-1">
                  {values.storeInformation.city}, {storeState} {values.storeInformation.zipCode}
                </div>
                <div className="pb-1">
                  {values.storeInformation.phone}
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" className="mb-4">
                  Billing Information
                </Typography>
                <Typography variant="body1">
                  Credit Card Number
                </Typography>
                <Typography variant="body1" className="ml-2">
                  xxxx-{values.billingInformation.ccNumber.slice(-4)}
                </Typography>
                <Typography variant="body1">
                  Expiration Date
                </Typography>
                <Typography variant="body1" className="ml-2">
                  {values.billingInformation.expiry}
                </Typography>
                <Typography variant="body1">
                  {values.billingInformation.firstName}
                  {' '}
                  {values.billingInformation.lastName}
                </Typography>
                <Typography variant="body1">
                  {values.billingInformation.company}
                </Typography>
                <Typography variant="body1">
                  {values.billingInformation.streetAddress1}
                </Typography>
                {values.billingInformation.streetAddress2 &&
                  <Typography variant="body1">
                    {values.billingInformation.streetAddress2}
                  </Typography>
                }
                <Typography variant="body1">
                  {values.billingInformation.city}, {billingState} {values.billingInformation.zipCode}
                </Typography>
                <Typography variant="body1" className="mb-8">
                  {values.billingInformation.phone}
                </Typography>
              </Grid>
            </Grid>
          </Card>
          <Card elevation={25} className="px-5 py-7 bg-light-blue">
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Typography variant="h5" className="uppercase font-bold">
                  total
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h5" className="capitalize font-bold">
                  Store Setup Fee:{' '}
                  <NumberFormat
                    value={onboarding.totalOneTimePrice + marketing.totalOneTimePrice}
                    displayType="text"
                    thousandSeparator={','}
                    decimalSeparator={'.'}
                    prefix={'$'}
                    fixedDecimalScale
                    decimalScale={2}
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography variant="h5" className="font-bold">
                  Monthly Subscription:{' '}
                  <NumberFormat
                    value={monthlySubscriptionPrice}
                    displayType="text"
                    thousandSeparator={','}
                    decimalSeparator={'.'}
                    prefix={'$'}
                    fixedDecimalScale
                    decimalScale={2}
                  />
                  <Typography variant="h6" className="h6 mt-2">
                    {plan === 'annually' &&
                      <NumberFormat
                        className="h6 font-semibold"
                        value={monthlySubscriptionPrice * 12}
                        displayType="text"
                        thousandSeparator={','}
                        decimalSeparator={'.'}
                        prefix={'billed at $'}
                        suffix={'/yr'}
                        fixedDecimalScale
                        decimalScale={2}
                      />}
                    {plan === 'semiAnnually' &&
                      <NumberFormat
                        className="h6 font-semibold"
                        value={monthlySubscriptionPrice * 6}
                        displayType="text"
                        thousandSeparator={','}
                        decimalSeparator={'.'}
                        prefix={'billed at $'}
                        suffix={' every 6 month'}
                        fixedDecimalScale
                        decimalScale={2}
                      />}
                    {plan === 'monthly' &&
                      <NumberFormat
                        className="h6 font-semibold"
                        value={monthlySubscriptionPrice}
                        displayType="text"
                        thousandSeparator={','}
                        decimalSeparator={'.'}
                        prefix={'billed monthly $'}
                        fixedDecimalScale
                        decimalScale={2}
                      />}
                  </Typography>
                </Typography>
              </Grid>
            </Grid>
          </Card>
          <div className="flex my-5 mx-3">
            <Typography variant="body1" className="text-20 font-bold mr-2">
              {'NOTE:'}
            </Typography>
            <Typography variant="body1" className="text-20 font-bold">
              {`You will only be charged the setup fee.
                You will have the option to browse various
                  marketing plans and other services by DermPRO
                prior to your store going live. At the time
                  your store goes LIVE you will have various
                payment options for your subscription.`}
            </Typography>
          </div>
          <Grid item xs={12} className="mt-5 px-5">
            <FormControlLabel
              className="align-center"
              control={
                <Checkbox
                  size="small"
                  name="isTermsAccepted"
                  color="primary"
                  checked={values.isTermsAccepted}
                  onChange={handleChange}
                />
              }
              label={
                <strong
                  className="flex mt-1"
                >
                  <span>I Accept All </span>
                  <Typography
                    component="a"
                    className="underline font-bold ml-1 text-primary-hover"
                    onClick={(event) => {
                      event.preventDefault();
                      toggleOpenTerms(!openTerms);
                    }}
                  >
                    Terms And Conditions
                  </Typography>
                  <span className="text-error">*</span>
                </strong>
              }
            />
          </Grid>
        </Grid>
      </Grid>
      <Dialog
        fullScreen
        open={openTerms}
        className="scroll-y w-full-screen max-w-full"
      >
        <Grid container className="px-3 py-5 bg-primary text-white items-center sticky">
          <Grid item xs={11} className="text-center">
            <Typography variant="h1" className="h1 font-normal">Terms and Conditions</Typography>
          </Grid>
          <Grid item xs={1} className="text-center">
            <IconButton
              onClick={() => toggleOpenTerms(!openTerms)}
            >
              <Icon className="text-white">close</Icon>
            </IconButton>
          </Grid>
        </Grid>
        <div className="max-w-1200 mx-auto my-8">
          <TermsAndConditions />
        </div>
      </Dialog>
    </>
  );
};

export default StepTree;
